import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { track } from "../lib/pixel";

const CartContext = createContext(null);
const KEY = "rsa_cart_v1";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((product, quantity = 1, variantLabel = null) => {
    // Resolve variant (size/mg option) pricing when provided.
    const variants = product.variants || [];
    const v = variantLabel
      ? variants.find((x) => x.label === variantLabel) || variants[product.default_variant || 0]
      : variants[product.default_variant || 0];
    const key = variantLabel ? `${product.handle}::${variantLabel}` : product.handle;
    const dose = v ? v.label : product.dose;
    const price = v ? v.price : product.price;
    setItems((prev) => {
      const found = prev.find((i) => (i.key || i.handle) === key);
      if (found) {
        return prev.map((i) =>
          (i.key || i.handle) === key ? { ...i, quantity: i.quantity + quantity } : i
        );
      }
      return [
        ...prev,
        {
          key,
          handle: product.handle,
          variant: variantLabel,
          name: product.name,
          dose,
          price,
          image: product.images?.[0],
          quantity,
        },
      ];
    });
    setOpen(true);
    track("AddToCart", {
      content_name: `${product.name} ${dose}`,
      content_ids: [key],
      value: price,
      currency: "AUD",
    });
  }, []);

  const updateQty = useCallback((key, quantity) => {
    setItems((prev) =>
      quantity <= 0
        ? prev.filter((i) => (i.key || i.handle) !== key)
        : prev.map((i) => ((i.key || i.handle) === key ? { ...i, quantity } : i))
    );
  }, []);

  const removeItem = useCallback((key) => {
    setItems((prev) => prev.filter((i) => (i.key || i.handle) !== key));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const count = items.reduce((s, i) => s + i.quantity, 0);
  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const distinctCount = items.length;

  return (
    <CartContext.Provider
      value={{ items, addItem, updateQty, removeItem, clear, count, subtotal, distinctCount, open, setOpen }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
